import { Box, Typography, Button, alpha } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { PageLayout, PageSection, GlassCard } from '@paneon/ui';
import { useTheme } from '@paneon/theme';
import { useAuth } from '@paneon/auth';

/**
 * Dashboard page with widget grid
 */
export function Dashboard() {
  const { currentTheme, isDarkMode } = useTheme();
  const { user } = useAuth();

  const greeting = getGreeting();

  return (
    <PageLayout
      title="Dashboard"
      rightAction={
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          size="small"
          sx={{
            borderRadius: 2,
            textTransform: 'none',
          }}
        >
          Widget
        </Button>
      }
    >
      {/* Logo & Greeting */}
      <PageSection>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
          <Box
            component="img"
            src="/logo.png"
            alt="Paneon"
            sx={{
              width: 48,
              height: 'auto',
            }}
          />
          <Typography
            variant="h5"
            sx={{
              fontWeight: 600,
              color: isDarkMode ? '#fff' : '#000',
            }}
          >
            {greeting}, {user?.name?.split(' ')[0] || 'Tierfreund'}!
          </Typography>
        </Box>
        <Typography
          variant="body2"
          sx={{
            color: isDarkMode ? alpha('#fff', 0.7) : alpha('#000', 0.7),
          }}
        >
          Verwalte die Ernährung deiner Haustiere
        </Typography>
      </PageSection>

      {/* Quick Actions */}
      <PageSection title="Schnellaktionen">
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <QuickActionCard
            title="Fütterung"
            subtitle="Jetzt eintragen"
            icon="🍖"
            color={currentTheme.colors.primary}
          />
          <QuickActionCard
            title="Haustier"
            subtitle="Hinzufügen"
            icon="🐾"
            color={currentTheme.colors.secondary}
          />
          <QuickActionCard title="Gewicht" subtitle="Eintragen" icon="⚖️" color="#9C27B0" />
        </Box>
      </PageSection>

      {/* Widget Grid Placeholder */}
      <PageSection title="Deine Widgets">
        <GlassCard
          sx={{
            p: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 200,
          }}
        >
          <Typography
            variant="body1"
            sx={{
              color: isDarkMode ? alpha('#fff', 0.5) : alpha('#000', 0.5),
              mb: 2,
            }}
          >
            Füge Widgets hinzu um deinen Dashboard anzupassen
          </Typography>
          <Button variant="outlined" startIcon={<AddIcon />} sx={{ borderRadius: 2 }}>
            Widget hinzufügen
          </Button>
        </GlassCard>
      </PageSection>
    </PageLayout>
  );
}

/**
 * Quick action card component
 */
function QuickActionCard({
  title,
  subtitle,
  icon,
  color,
}: {
  title: string;
  subtitle: string;
  icon: string;
  color: string;
}) {
  const { isDarkMode } = useTheme();

  return (
    <GlassCard
      onClick={() => {}}
      sx={{
        p: 2,
        flex: '1 1 150px',
        minWidth: 150,
        cursor: 'pointer',
      }}
    >
      <Box
        sx={{
          width: 48,
          height: 48,
          borderRadius: 2,
          backgroundColor: alpha(color, 0.1),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: 1.5,
          fontSize: '24px',
        }}
      >
        {icon}
      </Box>
      <Typography
        variant="subtitle2"
        sx={{
          fontWeight: 600,
          color: isDarkMode ? '#fff' : '#000',
        }}
      >
        {title}
      </Typography>
      <Typography
        variant="caption"
        sx={{
          color: isDarkMode ? alpha('#fff', 0.6) : alpha('#000', 0.6),
        }}
      >
        {subtitle}
      </Typography>
    </GlassCard>
  );
}

/**
 * Get time-based greeting
 */
function getGreeting(): string {
  const hour = new Date().getHours();
  if (hour < 12) return 'Guten Morgen';
  if (hour < 18) return 'Guten Tag';
  return 'Guten Abend';
}
